import { Button, Toast } from "@shopify/polaris"
import { usePostHog } from "posthog-js/react"
import React, { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { LanguageSelector } from "../components/LanguageSelector.jsx"
import { OnboardingTimeline } from "../components/OnboardingTimeline.jsx"
import { RandomAppNameButton } from "../components/RandomAppNameButton.jsx"
import { Spinner } from "../components/Spinner.jsx"
import { TopMenu } from "../components/TopMenu.jsx"
import { DynamicContainer } from "../components/linkStore/DynamicContainer.jsx"
import { Step0 } from "../components/linkStore/Step0.jsx"
import { Step1 } from "../components/linkStore/Step1.jsx"
import { Step2 } from "../components/linkStore/Step2.jsx"
import { Step3 } from "../components/linkStore/Step3.jsx"
import { GlobalStateContext } from "../components/providers/GlobalStateProvider.jsx"
import { useElementDimensions } from "../hooks/useElementDimensions.js"
import {
	useRemoveSavedStore,
	useUpdateUser,
} from "../hooks/useFirestoreUserHooks.js"
import {
	useLinkEmbeddedStoreHandoff,
	useLinkStore,
} from "../hooks/useLinkStore.js"
import { useMediaQuery } from "../hooks/useMediaQuery.js"
import { useUpdateSubscriptionMetadata } from "../hooks/usePaddleHooks.js"
import { generateRandomAppName } from "../utils/generateRandomAppName.js"

const LinkStore = ({ newUser = false }) => {
	const posthog = usePostHog()
	const { state, dispatch } = useContext(GlobalStateContext)
	const navigate = useNavigate()
	const { t } = useTranslation()
	const updateUser = useUpdateUser()
	const linkStore = useLinkStore()
	const linkEmbeddedStoreHandoff = useLinkEmbeddedStoreHandoff()
	const updateSubscriptionMetadata = useUpdateSubscriptionMetadata()
	const removeSavedStore = useRemoveSavedStore()
	const isMobile = useMediaQuery("(max-width: 500px)")
	const { right: containerRight, ref: containerRef } = useElementDimensions()

	const [accessToken, setAccessToken] = useState("")
	const [step, setStep] = useState(0)

	const [loadingLinking, setLoadingLinking] = useState(false)
	const [errorLinking, setErrorLinking] = useState(false)
	const [successLinking, setSuccessLinking] = useState(false)
	const [showErrorLinkingToast, setShowErrorLinkingToast] = useState(false)
	const [errorLinkingToastContent, setErrorLinkingToastContent] = useState("")

	const [showOverrideShopDomainInput, setShowOverrideShopDomainInput] =
		useState(false)
	const [overrideShopDomain, setOverrideShopDomain] = useState("")
	const [randomAppName, setRandomAppName] = useState("")

	const hasOtherStoresLinked = Object.keys(state.stores).length > 0

	const displayErrorToast = ({
		userCheckedScopes,
		requiredScopes,
		errorCode,
	}) => {
		setShowErrorLinkingToast(true)
		setErrorLinkingToastContent(
			<div>
				{errorCode === "invalid_scopes_entered_error" ? (
					<div style={{ padding: "0.5rem 0rem" }}>
						<div style={{ marginBottom: "1rem" }}>
							{t(
								"link_store.the_following_scopes_are_required_to_link_your_store",
							)}
						</div>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: "0.3rem",
							}}
						>
							{requiredScopes.map((scope, i) => (
								<div
									key={i}
									style={{
										display: "flex",
										alignItems: "center",
										gap: "0.3rem",
									}}
								>
									{CheckedIcon}
									<code>{scope}</code>
								</div>
							))}
						</div>
						<div
							style={{ marginTop: "1rem", marginBottom: "1rem" }}
						>
							{t(
								"link_store.you_have_granted_the_following_scopes",
							)}
						</div>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: "0.3rem",
							}}
						>
							{userCheckedScopes.map((scope, i) => (
								<div
									key={i}
									style={{
										display: "flex",
										alignItems: "center",
										gap: "0.3rem",
									}}
								>
									{DontCheckIcon}
									<code>{scope}</code>
								</div>
							))}
						</div>
						<div style={{ marginTop: "1rem" }}>
							{t(
								"link_store.please_enable_the_correct_scopes_and_try_again",
							)}
						</div>
					</div>
				) : errorCode === "no_scopes_returned_error" ? (
					<div>
						{t(
							"link_store.error_linking_store_check_that_the_myshopify_com_domain_and_the_token_entered_are_the_correct",
						)}
					</div>
				) : errorCode === "store_already_linked_to_another_user" ? (
					<div>
						{t(
							"link_store.error_linking_store_the_store_you_are_trying_to_link_is_already_linked_to_another_user_if_you_are_a_store_staff_employee_make_the_store_owner_add_you_as_a_staff_to_obtain_access",
						)}
					</div>
				) : (
					<div>
						{t("link_store.error_linking_store_unexpected_error")}
					</div>
				)}
			</div>,
		)
	}

	const hideErrorToast = () => {
		setShowErrorLinkingToast(false)
		setErrorLinkingToastContent("")
	}

	const link = async (at, isEmbeddedAppAccessToken = false) => {
		if (loadingLinking || errorLinking || successLinking) return
		hideErrorToast()

		const store = overrideShopDomain || state.shop
		const cleanedAccessToken = at.trim()
		const activeSubscriptionId = newUser
			? state.otherKopyBasicSubscriptions?.[0]?.id ||
				state.otherKopyProtectSubscriptions?.[0]?.id ||
				null
			: null

		setLoadingLinking(true)
		const { success, userCheckedScopes, requiredScopes, errorCode } =
			await linkStore({
				userId: state.userId,
				store,
				accessToken: cleanedAccessToken,
				isEmbeddedAppAccessToken,
				activeSubscriptionId,
			})
		setLoadingLinking(false)

		if (success) {
			setSuccessLinking(true)
			if (overrideShopDomain !== state.shop) {
				await removeSavedStore(state.userId, state.shop)
			}
			await updateUser(state.userId, {
				hasCompletedOnBoarding: true,
			})
			posthog?.capture("user_links_store_v2", { store: store })
			localStorage.removeItem("eapp_shop")
			localStorage.removeItem("eapp_handoff")

			setTimeout(() => {
				if (newUser) {
					return window.location.reload()
				} else {
					setSuccessLinking(false)

					dispatch({
						stores: {
							...state.stores,
							[store]: cleanedAccessToken,
						},
						shop: store,
						accessToken: cleanedAccessToken,
						savedStores: state.savedStores.filter(
							(savedStore) => savedStore !== store,
						),
					})

					navigate("/")
				}
			}, 3000)
		} else {
			setErrorLinking(true)
			setTimeout(() => {
				setErrorLinking(false)
			}, 3000)

			displayErrorToast({ userCheckedScopes, requiredScopes, errorCode })

			setShowOverrideShopDomainInput(true)
		}
	}

	const linkFromEmbeddedHandoff = async () => {
		if (loadingLinking || errorLinking || successLinking) return
		hideErrorToast()

		const store = localStorage.getItem("eapp_shop")
		if (window.__kopyEappHandoffPromise) {
			await window.__kopyEappHandoffPromise
		}

		const activeSubscriptionId = newUser
			? state.otherKopyBasicSubscriptions?.[0]?.id ||
				state.otherKopyProtectSubscriptions?.[0]?.id ||
				null
			: null

		setLoadingLinking(true)
		const { success, userCheckedScopes, requiredScopes, errorCode } =
			await linkEmbeddedStoreHandoff({
				userId: state.userId,
				store,
				activeSubscriptionId,
			})
		setLoadingLinking(false)

		if (success) {
			setSuccessLinking(true)
			if (overrideShopDomain !== state.shop) {
				await removeSavedStore(state.userId, state.shop)
			}
			await updateUser(state.userId, {
				hasCompletedOnBoarding: true,
			})
			posthog?.capture("user_links_store_v2", { store })
			localStorage.removeItem("eapp_shop")
			localStorage.removeItem("eapp_handoff")

			setTimeout(() => {
				window.location.reload()
			}, 3000)
		} else {
			setErrorLinking(true)
			setTimeout(() => {
				setErrorLinking(false)
			}, 3000)

			displayErrorToast({ userCheckedScopes, requiredScopes, errorCode })
			setShowOverrideShopDomainInput(true)
		}
	}

	useEffect(() => {
		if (window?.dataLayer && !hasOtherStoresLinked) {
			window.dataLayer.push({ event: "onboarding_success" })
		}

		setRandomAppName(generateRandomAppName() || "Master Sync")
	}, [])

	useEffect(async () => {
		if (state.shop && !loadingLinking) {
			if (
				localStorage.getItem("eapp_shop") &&
				localStorage.getItem("eapp_handoff") &&
				!state.stores[localStorage.getItem("eapp_shop")] &&
				state.shop === localStorage.getItem("eapp_shop")
			) {
				setStep(3)
				await linkFromEmbeddedHandoff()
			}
		}
	}, [state.shop])

	const CheckedIcon = (
		<svg
			style={{ display: "block", width: "1.3rem", height: "1.3rem" }}
			viewBox="0 0 24 24"
		>
			<path
				fill="currentColor"
				d="M6.25 3A3.25 3.25 0 0 0 3 6.25v11.5A3.25 3.25 0 0 0 6.25 21h11.5A3.25 3.25 0 0 0 21 17.75V6.25A3.25 3.25 0 0 0 17.75 3H6.25ZM4.5 6.25c0-.966.784-1.75 1.75-1.75h11.5c.966 0 1.75.784 1.75 1.75v11.5a1.75 1.75 0 0 1-1.75 1.75H6.25a1.75 1.75 0 0 1-1.75-1.75V6.25Zm12.78 3.03a.75.75 0 1 0-1.06-1.06l-6.223 6.216L7.78 12.22a.75.75 0 0 0-1.06 1.06l2.745 2.746a.75.75 0 0 0 1.06 0l6.754-6.745Z"
			></path>
		</svg>
	)

	const DontCheckIcon = (
		<svg
			style={{ display: "block", width: "1.3rem", height: "1.3rem" }}
			viewBox="0 0 24 24"
		>
			<path
				fill="currentColor"
				d="M12 4a8 8 0 1 0 0 16a8 8 0 0 0 0-16zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12zm5.793-4.207a1 1 0 0 1 1.414 0L12 10.586l2.793-2.793a1 1 0 1 1 1.414 1.414L13.414 12l2.793 2.793a1 1 0 0 1-1.414 1.414L12 13.414l-2.793 2.793a1 1 0 0 1-1.414-1.414L10.586 12L7.793 9.207a1 1 0 0 1 0-1.414z"
			></path>
		</svg>
	)

	const hasAnotherStoreLinked = Object.keys(state.stores).length > 0

	return (
		<div
			style={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				padding: "0rem 1rem",
			}}
		>
			<TopMenu accountSelector storeSelector />

			<div
				style={{
					display: "flex",
					gap: "1rem",
					justifyContent: "center",
					alignItems: "center",
					position: "relative",
					width: "100%",
				}}
			>
				<div
					className="border shadow"
					ref={containerRef}
					style={{
						marginTop: "5rem",
						marginBottom: "5rem",
						maxWidth: isMobile ? "100%" : "480px",
						height: "auto",
						padding: "1.5rem",
						background: "#fff",
						borderRadius: "2rem",
						flex: "1",
						display: "flex",
						flexDirection: "column",
						transition: "height 0.5s ease",
						pointerEvents: loadingLinking ? "none" : "all",
					}}
				>
					<DynamicContainer initialWidth={500}>
						{step === 0 ? (
							<Step0 />
						) : step === 1 ? (
							<Step1
								randomAppNameButton={
									<RandomAppNameButton
										randomAppName={randomAppName}
									/>
								}
							/>
						) : step === 2 ? (
							<Step2
								randomAppNameButton={
									<RandomAppNameButton
										randomAppName={randomAppName}
									/>
								}
							/>
						) : step === 3 ? (
							<Step3
								randomAppNameButton={
									<RandomAppNameButton
										randomAppName={randomAppName}
									/>
								}
								accessToken={accessToken}
								setAccessToken={setAccessToken}
								showOverrideShopDomainInput={
									showOverrideShopDomainInput
								}
								overrideShopDomain={overrideShopDomain}
								setOverrideShopDomain={setOverrideShopDomain}
							/>
						) : (
							""
						)}
					</DynamicContainer>

					<div style={{ marginTop: "2rem" }}>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent:
									step === 0 ? "end" : "space-between",
								gap: "1rem",
							}}
						>
							{step > 0 && (
								<Button
									onClick={
										step === 0
											? null
											: () => setStep(step - 1)
									}
								>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "0.3rem",
											color: "#222",
										}}
									>
										<svg
											style={{
												height: "1rem",
												width: "1rem",
											}}
											viewBox="0 0 512 512"
										>
											<path
												d="M401.4 224h-214l83-79.4c11.9-12.5 11.9-32.7 0-45.2s-31.2-12.5-43.2 0L89 233.4c-6 5.8-9 13.7-9 22.4v.4c0 8.7 3 16.6 9 22.4l138.1 134c12 12.5 31.3 12.5 43.2 0 11.9-12.5 11.9-32.7 0-45.2l-83-79.4h214c16.9 0 30.6-14.3 30.6-32 .1-18-13.6-32-30.5-32z"
												fill="currentColor"
											></path>
										</svg>
										<span
											style={{
												fontSize: "0.9rem",
												fontWeight: "600",
											}}
										>
											{t("common.previous")}
										</span>
									</div>
								</Button>
							)}
							{step === 3 ? (
								<Button
									onClick={
										step === 3
											? () => link(accessToken)
											: () => setStep(step + 1)
									}
									disabled={!accessToken}
								>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "0.3rem",
											color: "#222",
										}}
									>
										{loadingLinking ? (
											<Spinner />
										) : errorLinking ? (
											<div style={{ color: "red" }}>
												{DontCheckIcon}
											</div>
										) : successLinking ? (
											<div style={{ color: "green" }}>
												{CheckedIcon}
											</div>
										) : (
											""
										)}
										<div
											style={{
												fontSize: "0.9rem",
												fontWeight: "600",
											}}
										>
											{loadingLinking
												? t("link_store.linking")
												: errorLinking
													? t(
															"link_store.error_linking",
														)
													: successLinking
														? t(
																"link_store.store_linked",
															)
														: t(
																"link_store.link_store",
															)}
										</div>
									</div>
								</Button>
							) : (
								<Button
									onClick={
										step === 3
											? null
											: () => setStep(step + 1)
									}
								>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "0.3rem",
											color: "#222",
										}}
									>
										<span
											style={{
												fontSize: "0.9rem",
												fontWeight: "600",
											}}
										>
											{t("common.next")}
										</span>
										<svg
											width="1rem"
											height="1rem"
											viewBox="0 0 512 512"
										>
											<path
												d="M284.9 412.6l138.1-134c6-5.8 9-13.7 9-22.4v-.4c0-8.7-3-16.6-9-22.4l-138.1-134c-12-12.5-31.3-12.5-43.2 0-11.9 12.5-11.9 32.7 0 45.2l83 79.4h-214c-17 0-30.7 14.3-30.7 32 0 18 13.7 32 30.6 32h214l-83 79.4c-11.9 12.5-11.9 32.7 0 45.2 12 12.5 31.3 12.5 43.3 0z"
												fill="currentColor"
											></path>
										</svg>
									</div>
								</Button>
							)}
						</div>

						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: "100%",
								gap: "0.5rem",
							}}
						>
							{Array.from({ length: 4 }).map((_, i) => (
								<div
									key={`step-indicator-${i}`}
									style={{
										height:
											step === i ? "0.7rem" : "0.5rem",
										width: step === i ? "0.7rem" : "0.5rem",
										border:
											step === i
												? "2px solid #ccc"
												: "none",
										background:
											step === i ? "#fff" : "#ccc",
										borderRadius: "100%",
									}}
								></div>
							))}
						</div>
					</div>
				</div>

				<div
					className="float-right"
					style={{
						left: `calc(${containerRight}px + 0.2rem)`,
						top: "7rem",
						paddingRight: "1rem",
					}}
				>
					<OnboardingTimeline step={3} newUser={newUser} />
					<style>{`
						.float-right {
							position: absolute;
							width: max-content;
						}

						@media (max-width: 900px) {
							.float-right {
								display: none;
							}
						}
						@media (max-width: 1200px) {
							.float-right {
								width: fit-content;
							}
						}
					`}</style>
				</div>
			</div>

			{showErrorLinkingToast && (
				<Toast
					content={errorLinkingToastContent}
					error
					onDismiss={() => hideErrorToast()}
					duration={86400000}
				/>
			)}

			<div
				style={{
					position: "fixed",
					bottom: "2rem",
					left: "2rem",
					zIndex: "500",
				}}
			>
				<LanguageSelector />
			</div>
		</div>
	)
}

export default LinkStore
